import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as api from '../utils/axiosRequests';

const DISMISSED_REASON_KEY = 'dismissedSuspensionReason';

const initialState = {
  status: 'idle',
  maintenance: 'false',
  id: null,
  ubcId: null,
  firstName: '',
  lastName: '',
  address: {},
  email: '',
  phone: '',
  birthday: '',
  rightholders: [],
  createdAt: '',
  contract: '',
  contract_insured: null,
  iban: '',
  bic: '',
  teletransmission: null,
  secuNumber: '',
  data: {},
  startDate: '',
  contract_option_resiliated_at: '',
  option_available: false,
  organism: '',
  mutualisation: '',
  complexCollege: true,
  last_contract_situation: '',
  files: {},
  rightholders_allowed: false,
  isSuspended: false,
  suspensionReason: '',
  showSuspensionModal: false,
};

const safeLocalStorageGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.error("Erreur d'accès localStorage (getItem):", e);
    return null;
  }
};

const safeLocalStorageSet = (key, value) => {
  try {
    if (
      value === null ||
      typeof value === 'undefined' ||
      value === ''
    ) {
      safeLocalStorageRemove(key);
      return;
    }
    localStorage.setItem(key, value);
  } catch (e) {
    console.error("Erreur d'accès localStorage (setItem):", e);
  }
};

const safeLocalStorageRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.error("Erreur d'accès localStorage (removeItem):", e);
  }
};

export const getAccount = createAsyncThunk('getAccount', async () => {
  const response = await api.getAccount();
  return response.data;
});

export const updateAccount = createAsyncThunk(
  'updateAccount',
  async (data) => {
    const response = await api.updateAccount(data);
    return response.data;
  },
);

export const getFiles = createAsyncThunk('getFiles', async () => {
  const response = await api.getFiles();
  return response.data;
});

export const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    reset: (state) => {
      Object.assign(state, initialState);
      safeLocalStorageRemove(DISMISSED_REASON_KEY); // Optionnel: nettoyer au logout
    },
    closeSuspensionModal: (state, action) => {
      const reasonToDismiss = action.payload;
      state.showSuspensionModal = false;
      if (reasonToDismiss) {
        safeLocalStorageSet(DISMISSED_REASON_KEY, reasonToDismiss);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAccount.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getAccount.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.maintenance = payload.maintenance;
        state.id = payload.id;
        state.firstName = payload.first_name;
        state.lastName = payload.last_name;
        state.address = payload.address;
        state.email = payload.email;
        state.phone = payload.phone;
        state.birthday = payload.birthday;
        state.rightholders = payload.rightholders;
        state.contract = payload.college;
        state.contract_insured = payload.etiq_contract_insured;
        state.iban = payload.iban;
        state.bic = payload.bic;
        state.teletransmission = payload.teletransmission;
        state.secuNumber = payload.secu_number;
        state.createdAt = payload.created_at;
        state.startDate = payload.start_date;
        state.option_available = payload.option_available;
        state.organism = payload.organism;
        state.mutualisation = payload.mutualisation;
        state.complexCollege = payload.complexCollege;
        state.last_contract_situation =
          payload.last_contract_situation;
        state.rightholders_allowed = payload.rightholders_allowed;
        state.ubcId = payload.etiq_contract_insured
          ? payload.etiq_contract_insured.id
          : null;
        state.contract_option_resiliated_at =
          payload.contract_option_resiliated_at;
        state.data = payload;
        state.files = initialState.files;

        const isNowSuspended = payload?.is_suspended ?? false;
        const currentReason = isNowSuspended
          ? (payload?.suspension_reason ?? '')
          : '';

        state.isSuspended = isNowSuspended;
        state.suspensionReason = currentReason;

        if (!isNowSuspended) {
          safeLocalStorageRemove(DISMISSED_REASON_KEY);
          state.showSuspensionModal = false;
        } else {
          const dismissedReason = safeLocalStorageGet(
            DISMISSED_REASON_KEY,
          );
          if (currentReason && currentReason !== dismissedReason) {
            state.showSuspensionModal = true;
          } else {
            state.showSuspensionModal = false;
          }
        }
      })
      .addCase(getAccount.rejected, (state, action) => {
        state.status = 'failed';
      })
      .addCase(updateAccount.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateAccount.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.id = payload.id;
        state.firstName = payload.first_name;
        state.lastName = payload.last_name;
        state.address = payload.address;
        state.email = payload.email;
        state.phone = payload.phone;
        state.birthday = payload.birthday;
        state.rightholders = payload.rightholders;
        state.contract = payload.college;
        state.iban = payload.iban;
        state.bic = payload.bic;
        state.teletransmission = payload.teletransmission;
        state.secuNumber = payload.secu_number;
        state.startDate = payload.start_date;
        state.option_available = payload.option_available;
        state.organism = payload.organism;
        state.mutualisation = payload.mutualisation;
        state.complexCollege = payload.complexCollege;
        state.last_contract_situation =
          payload.last_contract_situation;
        state.rightholders_allowed = payload.rightholders_allowed;
        state.maintenance = payload.maintenance;
        state.data = payload;

        const isNowSuspended = payload?.is_suspended ?? false;
        const currentReason = isNowSuspended
          ? (payload?.suspension_reason ?? '')
          : '';

        state.isSuspended = isNowSuspended;
        state.suspensionReason = currentReason;

        if (!isNowSuspended) {
          safeLocalStorageRemove(DISMISSED_REASON_KEY);
          state.showSuspensionModal = false;
        } else {
          const dismissedReason = safeLocalStorageGet(
            DISMISSED_REASON_KEY,
          );
          if (currentReason && currentReason !== dismissedReason) {
            state.showSuspensionModal = true;
          } else {
            state.showSuspensionModal = false;
          }
        }
      })
      .addCase(updateAccount.rejected, (state, action) => {
        state.status = 'failed';
      })
      .addCase(getFiles.pending, (state) => {
        // state.status = 'loading';
      })
      .addCase(getFiles.fulfilled, (state, { payload }) => {
        // state.status = 'succeeded';
        state.files = payload;
      })
      .addCase(getFiles.rejected, (state, action) => {
        // state.status = 'failed';
      });
  },
});

export const { reset, closeSuspensionModal } = accountSlice.actions;

export const selectIsSuspended = (state) => state.account.isSuspended;
export const selectSuspensionReason = (state) =>
  state.account.suspensionReason;
export const selectShowSuspensionModal = (state) =>
  state.account.showSuspensionModal;

export default accountSlice.reducer;
